import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import InputLabel from "@material-ui/core/InputLabel";
import Chip from "@material-ui/core/Chip";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import Grid from "@material-ui/core/Grid";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";

import SendIcon from "@material-ui/icons/Send";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import VisibilityIcon from "@material-ui/icons/Visibility";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import SettingsIcon from "@material-ui/icons/Settings";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import SaveIcon from "@material-ui/icons/Save";
import ContactPhoneIcon from "@material-ui/icons/ContactPhone";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";

import api from "../../services/api";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  cardSummary: {
    padding: theme.spacing(1.5),
    borderRadius: 8,
    textAlign: "center",
    fontWeight: "bold",
  },
  textArea: {
    fontFamily: "monospace",
    fontSize: "0.85rem",
    "& .MuiOutlinedInput-multiline": {
      resize: "vertical",
    },
    "& textarea": {
      resize: "vertical",
    }
  },
  previewBox: {
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: theme.spacing(2),
    whiteSpace: "pre-wrap",
    fontFamily: "sans-serif",
    fontSize: "0.95rem",
    lineHeight: 1.5,
  },
  tabsRoot: {
    borderBottom: "1px solid #e2e8f0",
    marginBottom: theme.spacing(2),
  },
  heroButtonCard: {
    padding: theme.spacing(4, 3),
    borderRadius: 16,
    border: "2px solid #e2e8f0",
    backgroundColor: "#ffffff",
    cursor: "pointer",
    transition: "all 0.25s ease-in-out",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)",
    "&:hover": {
      transform: "translateY(-4px)",
      borderColor: "#7c3aed",
      boxShadow: "0 20px 25px -5px rgba(124, 58, 237, 0.1), 0 10px 10px -5px rgba(124, 58, 237, 0.04)",
    },
  },
  compactTabBtn: {
    flex: 1,
    padding: theme.spacing(1.2, 2),
    borderRadius: 10,
    fontWeight: "bold",
    fontSize: "0.95rem",
    textTransform: "none",
    border: "1px solid #cbd5e1",
    backgroundColor: "#f8fafc",
    color: "#475569",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  compactTabBtnActiveAgenda: {
    backgroundColor: "#eff6ff",
    borderColor: "#3b82f6",
    color: "#1d4ed8",
    boxShadow: "0 2px 4px rgba(59, 130, 246, 0.15)",
  },
  compactTabBtnActiveResgate: {
    backgroundColor: "#faf5ff",
    borderColor: "#a855f7",
    color: "#7e22ce",
    boxShadow: "0 2px 4px rgba(168, 85, 247, 0.15)",
  }
}));

const DEFAULT_CONFIRM_MSG = `💜 Obrigada por confirmar sua presença!

Seu atendimento com a Dra. Stefanie Viana está reservado com todo carinho. 💫

📌 Esse horário é exclusivo para você — garantido com sua confirmação.

⏳ Caso precise desmarcar, pedimos que nos avise com pelo menos 24h de antecedência. Assim conseguimos cuidar da agenda com respeito a todas as pacientes. 💖

❗ Faltas sem aviso ou justificativa dificultam novos agendamentos, que só poderão ser realizados com pagamento antecipado.

❗ Em caso de ausência em retorno, o benefício será cancelado.

💰 Valores pagos não são reembolsáveis em caso de nova ausência sem justificativa.

👩‍⚕️ Nossa equipe já está se preparando com muito carinho pra te receber!

Clínica Stefanie Viana
Cuidado Integral à Saúde da Mulher & Família`;

const DEFAULT_CANCEL_MSG = `Compreendo perfeitamente! 🌸

Agradecemos por nos avisar com antecedência. Já notifiquei nossa equipe e uma de nossas atendentes vai te chamar em instantes para te ajudar a escolher um novo dia e horário ideal para você! Tenha um ótimo dia! 💖`;

const DEFAULT_RESCUE_TEMPLATES = [
  {
    id: "t1",
    title: "Novidade Especial (Curto & Curioso)",
    body: "Olá, {nome}! Tudo bem? Passando rapidinho porque temos uma novidade especial aqui na {clinica}. Você está por aí? 😊"
  },
  {
    id: "t2",
    title: "Check-up & Saúde da Mulher",
    body: "Oi, {nome}, bom dia! Estava revisando nossos atendimentos aqui na {clinica} e lembrei com muito carinho de você. Já faz um tempinho desde sua última consulta ou exame. Como você está se sentindo? 🌸"
  },
  {
    id: "t3",
    title: "Abertura de Novos Horários",
    body: "Olá, {nome}! Tudo bem? Passando para avisar que abrimos novos horários de atendimento nesta semana aqui na {clinica}. Gostaria de aproveitar para verificar a sua disponibilidade com nossa equipe? ✨"
  }
];

const DEFAULT_PRENATAL_ALERT = `🤰 *Informativo para gestantes:*
Para o atendimento, pedimos que traga:
📋 Cartão de pré-natal
🩺 Primeiro ultrassom realizado
💉 Cartão de vacinação

Esses documentos são importantes para que a equipe médica possa acompanhar e avaliar melhor a sua gestação. 💕`;

const ConfirmarAgenda = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  // Aba selecionada: null = Tela Inicial Vazia com 2 Botões Grandes; 0 = Confirmar Agenda; 1 = Resgatar Pacientes
  const [activeTab, setActiveTab] = useState(null);

  const [whatsapps, setWhatsapps] = useState([]);
  const [selectedWhatsappId, setSelectedWhatsappId] = useState("");
  const [rawText, setRawText] = useState("");
  const [loadingParse, setLoadingParse] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [sendingIndex, setSendingIndex] = useState(null);
  const [sendingBatch, setSendingBatch] = useState(false);
  const [previewMsg, setPreviewMsg] = useState(null);

  // Campos opcionais para listas rápidas (Nome + Telefone)
  const [defaultDate, setDefaultDate] = useState("");
  const [defaultTime, setDefaultTime] = useState("");
  const [defaultService, setDefaultService] = useState("");
  const [defaultProfessional, setDefaultProfessional] = useState("Dra. Stefanie Viana");
  const [clinicName, setClinicName] = useState("Clínica Stefanie Viana");
  const [isHumanSupport, setIsHumanSupport] = useState(false);

  // Modelos de Resposta IA e Avisos
  const [msgConfirmada, setMsgConfirmada] = useState(
    () => localStorage.getItem("template_confirmado") || DEFAULT_CONFIRM_MSG
  );
  const [msgCancelada, setMsgCancelada] = useState(
    () => localStorage.getItem("template_cancelado") || DEFAULT_CANCEL_MSG
  );
  const [prenatalAlertText, setPrenatalAlertText] = useState(
    () => localStorage.getItem("template_prenatal_alert") || DEFAULT_PRENATAL_ALERT
  );

  // Modelos de Resgate de Pacientes (salvos no localStorage)
  const [rescueTemplates, setRescueTemplates] = useState(() => {
    try {
      const saved = localStorage.getItem("rescue_templates");
      return saved ? JSON.parse(saved) : DEFAULT_RESCUE_TEMPLATES;
    } catch {
      return DEFAULT_RESCUE_TEMPLATES;
    }
  });

  const [selectedRescueTemplateId, setSelectedRescueTemplateId] = useState(() => {
    return rescueTemplates[0]?.id || "t1";
  });

  const [customRescueTitle, setCustomRescueTitle] = useState(() => {
    return rescueTemplates[0]?.title || DEFAULT_RESCUE_TEMPLATES[0].title;
  });

  const [customRescueText, setCustomRescueText] = useState(() => {
    return rescueTemplates[0]?.body || DEFAULT_RESCUE_TEMPLATES[0].body;
  });

  const applyConnectionDefaults = (wapp) => {
    if (!wapp) return;
    const isStefanie = wapp.name.toLowerCase().includes("csv");
    if (isStefanie) {
      setClinicName("Clínica Stefanie Viana");
      setDefaultProfessional("Dra. Stefanie Viana");
      setIsHumanSupport(false);
    } else {
      const cleanName = wapp.name.replace(/[-_]/g, " ").toUpperCase();
      setClinicName(cleanName);
      setDefaultProfessional("");
      setIsHumanSupport(true);
    }
  };

  useEffect(() => {
    const fetchWhatsapps = async () => {
      try {
        const { data } = await api.get("/whatsapp");
        setWhatsapps(data || []);
        if (data && data.length > 0) {
          // Se tiver apenas 1 conexão permitida, seleciona ela obrigatoriamente
          // Se tiver várias, prioriza uma que contenha 'externo', senão a primeira disponível da lista do usuário
          const defaultWapp = data.length === 1 ? data[0] : (data.find(w => w.name.toLowerCase().includes("externo")) || data[0]);
          setSelectedWhatsappId(defaultWapp.id);
          applyConnectionDefaults(defaultWapp);
        }
      } catch (err) {
        toastError(err);
      }
    };
    fetchWhatsapps();
  }, []);

  const handleWhatsappChange = (id) => {
    setSelectedWhatsappId(id);
    const selected = whatsapps.find(w => w.id === id);
    if (selected) {
      applyConnectionDefaults(selected);
    }
  };

  const handleSaveTemplates = () => {
    localStorage.setItem("template_confirmado", msgConfirmada);
    localStorage.setItem("template_cancelado", msgCancelada);
    localStorage.setItem("template_prenatal_alert", prenatalAlertText);

    // Atualiza mensagens das pacientes marcadas como gestantes se a lista já estiver carregada
    if (appointments && appointments.length > 0) {
      const updated = appointments.map((item) => {
        if (item.isPregnant) {
          return {
            ...item,
            formattedMessage: applyPrenatalAlertToMessage(item.originalMessage || item.formattedMessage, true)
          };
        }
        return item;
      });
      setAppointments(updated);
    }

    toast.success("Modelos e avisos salvos com sucesso!");
  };

  const handleSelectRescueTemplate = (tmplId) => {
    setSelectedRescueTemplateId(tmplId);
    const found = rescueTemplates.find(t => t.id === tmplId);
    if (found) {
      setCustomRescueTitle(found.title);
      setCustomRescueText(found.body);
    }
  };

  const handleSaveCurrentRescueTemplate = () => {
    if (!customRescueTitle.trim()) {
      toast.warning("O nome do modelo não pode ficar vazio.");
      return;
    }
    const updated = rescueTemplates.map(t => {
      if (t.id === selectedRescueTemplateId) {
        return {
          ...t,
          title: customRescueTitle.trim(),
          body: customRescueText
        };
      }
      return t;
    });
    setRescueTemplates(updated);
    localStorage.setItem("rescue_templates", JSON.stringify(updated));
    toast.success("Modelo e nome atualizados com sucesso!");
  };

  const handleAddRescueTemplate = () => {
    const newId = `t_${Date.now()}`;
    const newTitle = `Novo Modelo ${rescueTemplates.length + 1}`;
    const newTmpl = {
      id: newId,
      title: newTitle,
      body: "Olá, {nome}! Tudo bem? Passando aqui da {clinica}. Como você está?"
    };
    const updated = [...rescueTemplates, newTmpl];
    setRescueTemplates(updated);
    setSelectedRescueTemplateId(newId);
    setCustomRescueTitle(newTitle);
    setCustomRescueText(newTmpl.body);
    localStorage.setItem("rescue_templates", JSON.stringify(updated));
    toast.success("Novo modelo adicionado!");
  };

  const handleDeleteRescueTemplate = (tmplId) => {
    if (rescueTemplates.length <= 1) {
      toast.warning("Você precisa manter ao menos um modelo.");
      return;
    }
    const filtered = rescueTemplates.filter(t => t.id !== tmplId);
    setRescueTemplates(filtered);
    setSelectedRescueTemplateId(filtered[0].id);
    setCustomRescueTitle(filtered[0].title);
    setCustomRescueText(filtered[0].body);
    localStorage.setItem("rescue_templates", JSON.stringify(filtered));
    toast.info("Modelo removido.");
  };

  // Monta a mensagem personalizada de resgate para um paciente específico
  const buildRescueMessage = (patientName) => {
    const firstName = patientName ? patientName.split(" ")[0] : "Paciente";
    let text = customRescueText || "";
    text = text.replace(/{nome}/gi, firstName);
    text = text.replace(/{clinica}/gi, clinicName || "Nossa Clínica");
    return text;
  };

  // Injeta ou remove o alerta de pré-natal/gestante na mensagem de confirmação
  const applyPrenatalAlertToMessage = (baseMessage, isPregnant) => {
    if (!baseMessage) return "";
    // Remove qualquer versão anterior do alerta para evitar duplicações
    let cleanMessage = baseMessage;
    // O alerta fica entre a linha do procedimento/médico e a chamada para confirmar
    const alertPattern = /\n\n(?:🤰|📋)[\s\S]*?(?=\n\n(?:Digite \*1️⃣\*|Por favor, responda))/i;
    cleanMessage = cleanMessage.replace(alertPattern, "");

    if (!isPregnant || !prenatalAlertText?.trim()) {
      return cleanMessage;
    }

    const alertBlock = `\n\n${prenatalAlertText.trim()}`;

    // Insere o alerta antes da linha de instrução de confirmação
    if (cleanMessage.includes("Digite *1️⃣*")) {
      return cleanMessage.replace("\n\nDigite *1️⃣*", `${alertBlock}\n\nDigite *1️⃣*`);
    } else if (cleanMessage.includes("Por favor, responda")) {
      return cleanMessage.replace("\n\nPor favor, responda", `${alertBlock}\n\nPor favor, responda`);
    } else {
      return `${cleanMessage}${alertBlock}`;
    }
  };

  const handleTogglePregnant = (index) => {
    const updated = [...appointments];
    const item = updated[index];
    if (!item) return;

    const newIsPregnant = !item.isPregnant;
    item.isPregnant = newIsPregnant;
    item.formattedMessage = applyPrenatalAlertToMessage(item.originalMessage || item.formattedMessage, newIsPregnant);

    setAppointments(updated);
    if (newIsPregnant) {
      toast.info(`🤰 Informativo ativado para ${item.name || "a paciente"}`);
    } else {
      toast.info(`Informativo removido para ${item.name || "a paciente"}`);
    }
  };

  const handleToggleAllPregnant = () => {
    if (!appointments || appointments.length === 0) return;
    const allActive = appointments.every(a => a.isPregnant);
    const targetState = !allActive;

    const updated = appointments.map(item => ({
      ...item,
      isPregnant: targetState,
      formattedMessage: applyPrenatalAlertToMessage(item.originalMessage || item.formattedMessage, targetState)
    }));

    setAppointments(updated);
    if (targetState) {
      toast.info("🤰 Informativo ativado para todas as pacientes da lista");
    } else {
      toast.info("Informativo desativado para todas as pacientes");
    }
  };

  const handleParse = async () => {
    if (!rawText.trim()) {
      toast.warning("Cole o texto da agenda ou lista de contatos para processar.");
      return;
    }
    setLoadingParse(true);
    try {
      const isRescueMode = activeTab === 1;

      const { data } = await api.post("/appointments/parse", {
        rawText,
        defaultDate: isRescueMode ? "" : defaultDate,
        defaultTime: isRescueMode ? "" : defaultTime,
        defaultService: isRescueMode ? "Resgate de Paciente" : defaultService,
        defaultProfessional: isRescueMode ? "" : defaultProfessional,
        clinicName,
        isHumanSupport: isRescueMode ? true : isHumanSupport
      });

      if (!data.appointments || data.appointments.length === 0) {
        toast.info("Nenhum contato com dados suficientes foi identificado no texto.");
      } else {
        const uniqueAppointments = [];
        const seenKeys = new Set();

        for (const item of data.appointments) {
          const key = item.phone
            ? `${item.phone}_${item.date || ""}_${item.time || ""}`
            : `${item.name}_${item.date || ""}_${item.time || ""}`;

          if (!seenKeys.has(key)) {
            seenKeys.add(key);

            // Se estiver na aba Resgate, personaliza a mensagem com o modelo de resgate
            let finalMessage = isRescueMode
              ? buildRescueMessage(item.name)
              : item.formattedMessage;

            // Auto-detecta se o agendamento é de gestante, pré-natal ou ultrassom obstétrico
            const serviceLower = (item.serviceType || "").toLowerCase();
            const notesLower = (item.notes || "").toLowerCase();
            const rawItemText = `${serviceLower} ${notesLower}`;
            const isAutoPregnant = !isRescueMode && (
              rawItemText.includes("natal") ||
              rawItemText.includes("gestan") ||
              rawItemText.includes("gestat") ||
              rawItemText.includes("obstet") ||
              rawItemText.includes("obstét") ||
              rawItemText.includes("parto") ||
              rawItemText.includes("morfol") ||
              rawItemText.includes("transluc") ||
              (rawItemText.includes("ultrassom") && (rawItemText.includes("obst") || rawItemText.includes("gest") || rawItemText.includes("fetal"))) ||
              (rawItemText.includes("usg") && (rawItemText.includes("obst") || rawItemText.includes("gest") || rawItemText.includes("fetal")))
            );

            if (isAutoPregnant) {
              finalMessage = applyPrenatalAlertToMessage(finalMessage, true);
            }

            uniqueAppointments.push({
              ...item,
              originalMessage: item.formattedMessage,
              isPregnant: isAutoPregnant,
              formattedMessage: finalMessage,
              status: "pending"
            });
          }
        }

        setAppointments(uniqueAppointments);
        toast.success(`${uniqueAppointments.length} contato(s) processado(s) com sucesso!`);
      }
    } catch (err) {
      toastError(err);
    }
    setLoadingParse(false);
  };

  const handleSendSingle = async (index) => {
    const item = appointments[index];
    if (!item || !selectedWhatsappId) return;

    const updated = [...appointments];
    updated[index].status = "sending";
    setAppointments(updated);
    setSendingIndex(index);

    const isRescueMode = activeTab === 1;
    const targetStatus = isRescueMode ? "🟡 Resgate" : "🟣 Agendado";

    try {
      await api.post("/appointments/send-reminder", {
        name: item.name,
        phone: item.phone,
        whatsappId: selectedWhatsappId,
        message: item.formattedMessage,
        date: item.date,
        time: item.time,
        serviceType: isRescueMode ? "Resgate de Paciente" : item.serviceType,
        professional: item.professional,
        isHumanSupport: isRescueMode ? true : isHumanSupport,
        customStatus: targetStatus
      });

      const afterSend = [...appointments];
      afterSend[index].status = "sent";
      setAppointments(afterSend);
      toast.success(`Mensagem enviada para ${item.name}!`);
    } catch (err) {
      const afterSend = [...appointments];
      afterSend[index].status = "error";
      setAppointments(afterSend);
      toastError(err);
    }
    setSendingIndex(null);
  };

  const handleSendAll = async () => {
    if (!selectedWhatsappId) {
      toast.warning("Selecione a conexão do WhatsApp.");
      return;
    }
    setSendingBatch(true);

    for (let i = 0; i < appointments.length; i++) {
      if (appointments[i].status !== "sent") {
        await handleSendSingle(i);
        await new Promise((resolve) => setTimeout(resolve, 2500));
      }
    }

    setSendingBatch(false);
    toast.success("Todos os disparos pendentes foram concluídos!");
  };

  const handleClear = () => {
    setRawText("");
    setAppointments([]);
  };

  const total = appointments.length;
  const sentCount = appointments.filter(a => a.status === "sent").length;
  const pendingCount = total - sentCount;

  return (
    <MainContainer>
      <MainHeader>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <EventAvailableIcon style={{ color: "#7c3aed", fontSize: 28 }} />
          <Title>Disparos & Abordagem Ativa</Title>
        </div>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        {activeTab === null ? (
          /* TELA INICIAL: DOIS BOTÕES GRANDES LADO A LADO EM UMA LINHA SÓ */
          <div style={{ padding: "40px 20px", textAlign: "center" }}>
            <Typography variant="h5" style={{ fontWeight: 700, color: "#1e293b", marginBottom: 8 }}>
              Selecione o tipo de disparo
            </Typography>
            <Typography variant="body2" style={{ color: "#64748b", marginBottom: 32 }}>
              Escolha uma modalidade abaixo para carregar as ferramentas de disparo e configuração.
            </Typography>

            <div style={{ display: "flex", flexDirection: "row", gap: 20, width: "100%", maxWidth: 900, margin: "0 auto" }}>
              <Paper
                className={classes.heroButtonCard}
                style={{ flex: 1, border: "2px solid #3b82f6" }}
                onClick={() => setActiveTab(0)}
              >
                <div style={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  backgroundColor: "#eff6ff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 32
                }}>
                  🗓️
                </div>
                <Typography variant="h6" style={{ fontWeight: 700, color: "#1e40af" }}>
                  Confirmar Agenda
                </Typography>
                <Typography variant="body2" style={{ color: "#64748b" }}>
                  Disparo de lembrete e confirmação de presença para consultas agendadas do dia/semana.
                </Typography>
              </Paper>

              <Paper
                className={classes.heroButtonCard}
                style={{ flex: 1, border: "2px solid #a855f7" }}
                onClick={() => setActiveTab(1)}
              >
                <div style={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  backgroundColor: "#faf5ff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 32
                }}>
                  🎯
                </div>
                <Typography variant="h6" style={{ fontWeight: 700, color: "#6b21a8" }}>
                  Resgatar Pacientes
                </Typography>
                <Typography variant="body2" style={{ color: "#64748b" }}>
                  Abordagem ativa para pacientes sumidos ou antigos com roteiros e modelos de abordagem.
                </Typography>
              </Paper>
            </div>
          </div>
        ) : (
          <>
            {/* BARRA SUPERIOR COMPACTA EM UMA LINHA SÓ */}
            <div style={{ display: "flex", flexDirection: "row", gap: 12, marginBottom: 20, width: "100%" }}>
              <div
                className={`${classes.compactTabBtn} ${activeTab === 0 ? classes.compactTabBtnActiveAgenda : ""}`}
                onClick={() => {
                  setActiveTab(0);
                  setAppointments([]);
                  setRawText("");
                }}
              >
                <span style={{ fontSize: 18 }}>🗓️</span>
                <span>Confirmar Agenda & Presença</span>
              </div>

              <div
                className={`${classes.compactTabBtn} ${activeTab === 1 ? classes.compactTabBtnActiveResgate : ""}`}
                onClick={() => {
                  setActiveTab(1);
                  setAppointments([]);
                  setRawText("");
                }}
              >
                <span style={{ fontSize: 18 }}>🎯</span>
                <span>Resgatar Pacientes Antigos</span>
              </div>
            </div>

        {/* CABEÇALHO DE CONFIGURAÇÃO DA CONEXÃO */}
        <Grid container spacing={2} alignItems="center" style={{ marginBottom: 16 }}>
          <Grid item xs={12} sm={4}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel id="select-wapp-label">Conexão de Disparo</InputLabel>
              <Select
                labelId="select-wapp-label"
                value={selectedWhatsappId}
                onChange={(e) => handleWhatsappChange(e.target.value)}
                label="Conexão de Disparo"
              >
                {whatsapps.map((w) => (
                  <MenuItem key={w.id} value={w.id}>
                    📱 {w.name} {w.status === "CONNECTED" ? "🟢" : "🔴"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {activeTab === 0 ? (
            <Grid item xs={12} sm={4}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel id="select-mode-label">Modo de Pós-Envio</InputLabel>
                <Select
                  labelId="select-mode-label"
                  value={isHumanSupport ? "human" : "ai"}
                  onChange={(e) => setIsHumanSupport(e.target.value === "human")}
                  label="Modo de Pós-Envio"
                >
                  <MenuItem value="human">
                    🟢 Atendimento Humano (Sem IA / Fila Aberta)
                  </MenuItem>
                  <MenuItem value="ai">
                    🤖 Fluxo Automático IA (Clínica Stefanie)
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          ) : (
            <Grid item xs={12} sm={4}>
              <Chip
                icon={<ContactPhoneIcon style={{ color: "#047857" }} />}
                label="🟢 Pós-Envio: 100% Atendimento Humano (Status: 🟡 Resgate)"
                style={{ backgroundColor: "#ecfdf5", color: "#047857", fontWeight: "bold", width: "100%", height: 40 }}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={4}>
            <TextField
              label="Nome da Clínica / Assinatura"
              variant="outlined"
              size="small"
              fullWidth
              value={clinicName}
              onChange={(e) => setClinicName(e.target.value)}
              placeholder="Ex: GM Clinic, Stefanie Viana..."
            />
          </Grid>

          {total > 0 && (
            <>
              <Grid item xs={4} sm={2}>
                <Paper className={classes.cardSummary} style={{ backgroundColor: "#f1f5f9", color: "#334155" }}>
                  <Typography variant="caption">Total</Typography>
                  <Typography variant="h6">{total}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={4} sm={2}>
                <Paper className={classes.cardSummary} style={{ backgroundColor: "#ecfdf5", color: "#047857" }}>
                  <Typography variant="caption">Enviados</Typography>
                  <Typography variant="h6">{sentCount}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={4} sm={2}>
                <Paper className={classes.cardSummary} style={{ backgroundColor: "#fffbeb", color: "#b45309" }}>
                  <Typography variant="caption">Pendentes</Typography>
                  <Typography variant="h6">{pendingCount}</Typography>
                </Paper>
              </Grid>
            </>
          )}

          {sentCount > 0 && (
            <Grid item xs={12} sm={2} style={{ textAlign: "right" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => history.push("/tickets")}
                endIcon={<ArrowForwardIcon />}
                style={{ backgroundColor: "#7c3aed", color: "#fff", fontWeight: "bold" }}
              >
                Ver no Chat
              </Button>
            </Grid>
          )}
        </Grid>

        {/* CONTEÚDO ESPECÍFICO DA ABA 1: CONFIRMAR AGENDA */}
        {activeTab === 0 && (
          <>
            <div style={{ backgroundColor: "#f8fafc", padding: 12, borderRadius: 8, marginBottom: 16, border: "1px dashed #cbd5e1" }}>
              <Typography variant="caption" style={{ fontWeight: "bold", color: "#475569", display: "block", marginBottom: 8 }}>
                💡 Dica: Se for colar uma lista rápida (apenas Nome + Telefone), você pode preencher os campos abaixo para aplicar a todos:
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Data (opcional)"
                    placeholder="Ex: 25/09/2026"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={defaultDate}
                    onChange={(e) => setDefaultDate(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Horário Geral (opcional)"
                    placeholder="Ex: 09:00"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={defaultTime}
                    onChange={(e) => setDefaultTime(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Procedimento (opcional)"
                    placeholder="Ex: Consulta Ginecológica"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={defaultService}
                    onChange={(e) => setDefaultService(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Profissional"
                    placeholder="Dra. Stefanie Viana"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={defaultProfessional}
                    onChange={(e) => setDefaultProfessional(e.target.value)}
                  />
                </Grid>
              </Grid>
            </div>
          </>
        )}

        {/* CONTEÚDO ESPECÍFICO DA ABA 2: RESGATAR PACIENTES */}
        {activeTab === 1 && (
          <div style={{ backgroundColor: "#faf5ff", padding: 14, borderRadius: 8, marginBottom: 16, border: "1px solid #e9d5ff" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <Typography variant="subtitle2" style={{ fontWeight: "bold", color: "#6b21a8" }}>
                💬 Modelos de Mensagem de Abordagem / Resgate:
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddCircleOutlineIcon />}
                onClick={handleAddRescueTemplate}
                style={{ textTransform: "none", color: "#7c3aed", borderColor: "#7c3aed" }}
              >
                Adicionar Novo Modelo
              </Button>
            </div>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel id="select-rescue-template-label">Escolher Modelo</InputLabel>
                  <Select
                    labelId="select-rescue-template-label"
                    value={selectedRescueTemplateId}
                    onChange={(e) => handleSelectRescueTemplate(e.target.value)}
                    label="Escolher Modelo"
                  >
                    {rescueTemplates.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <div style={{ marginTop: 8, fontSize: "0.8rem", color: "#64748b" }}>
                  Variáveis suportadas: <strong>{"{nome}"}</strong> (Primeiro nome) e <strong>{"{clinica}"}</strong> (Nome da clínica).
                </div>
              </Grid>

              <Grid item xs={12} sm={8}>
                <TextField
                  label="Nome / Título do Modelo"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={customRescueTitle}
                  onChange={(e) => setCustomRescueTitle(e.target.value)}
                  placeholder="Ex: Abordagem Rápida, Checagem de Saúde..."
                  style={{ marginBottom: 12 }}
                />
                <TextField
                  label="Texto da Mensagem de Abordagem"
                  multiline
                  rows={8}
                  variant="outlined"
                  fullWidth
                  value={customRescueText}
                  onChange={(e) => setCustomRescueText(e.target.value)}
                  placeholder="Digite a mensagem de abordagem..."
                  className={classes.textArea}
                />
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 8 }}>
                  {rescueTemplates.length > 1 && (
                    <Button
                      size="small"
                      color="secondary"
                      onClick={() => handleDeleteRescueTemplate(selectedRescueTemplateId)}
                      startIcon={<DeleteOutlineIcon />}
                    >
                      Excluir Modelo
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleSaveCurrentRescueTemplate}
                    startIcon={<SaveIcon />}
                    style={{ backgroundColor: "#7c3aed", color: "#fff", fontWeight: "bold" }}
                  >
                    Salvar Alterações no Modelo
                  </Button>
                </div>
              </Grid>
            </Grid>
          </div>
        )}

        {/* ÁREA DE COLAR TEXTO BRUTO */}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label={
                activeTab === 0
                  ? "Cole aqui a lista de agendamentos copiada do sistema ou tabela de Nome e Telefone"
                  : "Cole aqui a lista de pacientes para resgate (Nome e Telefone)"
              }
              multiline
              rows={appointments.length > 0 ? 3 : 5}
              variant="outlined"
              fullWidth
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
              placeholder={
                activeTab === 0
                  ? "Exemplo:\n1) Detalhes do agendamento #134640696...\n2) ANDREA SILVEIRA  5585999172337..."
                  : "Exemplo:\nANDREA SILVEIRA  5585999172337\nDEBORA COUTO  5585999216292\n..."
              }
              className={classes.textArea}
            />
          </Grid>

          <Grid item xs={12} style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            {rawText && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleClear}
                startIcon={<DeleteOutlineIcon />}
              >
                Limpar
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={handleParse}
              disabled={loadingParse || !rawText.trim()}
              startIcon={loadingParse ? <CircularProgress size={20} color="inherit" /> : <FlashOnIcon />}
              style={{
                backgroundColor: activeTab === 0 ? "#0284c7" : "#7c3aed",
                color: "#fff",
                fontWeight: "bold"
              }}
            >
              {loadingParse
                ? "Processando..."
                : activeTab === 0
                ? "Processar Agendamentos"
                : "Processar Lista de Resgate"}
            </Button>
          </Grid>
        </Grid>

        {/* CONFIGURAÇÃO EXPANSÍVEL DE MODELOS DE RESPOSTA DA IA (SOMENTE ABA 1) */}
        {activeTab === 0 && (
          <div style={{ marginTop: 16, marginBottom: 16 }}>
            <Accordion variant="outlined" style={{ borderRadius: 8, backgroundColor: "#fafafa" }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <SettingsIcon style={{ color: "#64748b", fontSize: 20 }} />
                  <Typography style={{ fontWeight: 600, color: "#334155" }}>
                    ⚙️ Configurar Respostas Automáticas da IA (Confirmado / Cancelado)
                  </Typography>
                </div>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" style={{ fontWeight: "bold", color: "#047857", marginBottom: 6 }}>
                      ✅ Mensagem quando o Paciente CONFIRMA (Opção 1):
                    </Typography>
                    <TextField
                      multiline
                      rows={8}
                      variant="outlined"
                      fullWidth
                      value={msgConfirmada}
                      onChange={(e) => setMsgConfirmada(e.target.value)}
                      className={classes.textArea}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" style={{ fontWeight: "bold", color: "#b91c1c", marginBottom: 6 }}>
                      ❌ Mensagem quando o Paciente CANCELA / REMARCA (Opção 2):
                    </Typography>
                    <TextField
                      multiline
                      rows={8}
                      variant="outlined"
                      fullWidth
                      value={msgCancelada}
                      onChange={(e) => setMsgCancelada(e.target.value)}
                      className={classes.textArea}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" style={{ fontWeight: "bold", color: "#9333ea", marginBottom: 6 }}>
                      🤰 Aviso / Informativo para Gestantes (Pré-Natal & Ultrassom):
                    </Typography>
                    <TextField
                      multiline
                      rows={5}
                      variant="outlined"
                      fullWidth
                      value={prenatalAlertText}
                      onChange={(e) => setPrenatalAlertText(e.target.value)}
                      className={classes.textArea}
                      helperText="Este aviso será inserido automaticamente antes da instrução de confirmação apenas para as pacientes marcadas (Pré-Natal e Ultrassom Gestacional)."
                    />
                  </Grid>
                  <Grid item xs={12} style={{ textAlign: "right" }}>
                    <Button
                      variant="contained"
                      onClick={handleSaveTemplates}
                      startIcon={<SaveIcon />}
                      style={{ backgroundColor: "#0f172a", color: "#fff", fontWeight: "bold" }}
                    >
                      Salvar Modelos e Avisos
                    </Button>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </div>
        )}

        {/* TABELA DE CONTATOS PROCESSADOS */}
        {appointments.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <Typography variant="h6" style={{ fontWeight: "bold", color: "#1e293b" }}>
                📋 Lista de Disparos ({sentCount}/{total} enviados)
              </Typography>

              {pendingCount > 0 && (
                <Button
                  variant="contained"
                  onClick={handleSendAll}
                  disabled={sendingBatch}
                  startIcon={sendingBatch ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                  style={{ backgroundColor: "#16a34a", color: "#fff", fontWeight: "bold" }}
                >
                  {sendingBatch ? "Disparando em Lote..." : `🚀 Enviar Todos (${pendingCount} Pendentes)`}
                </Button>
              )}
            </div>

            <Table size="small" style={{ border: "1px solid #e2e8f0", borderRadius: 8 }}>
              <TableHead style={{ backgroundColor: "#f8fafc" }}>
                <TableRow>
                  <TableCell style={{ fontWeight: "bold" }}>Paciente</TableCell>
                  <TableCell style={{ fontWeight: "bold" }}>WhatsApp</TableCell>
                  <TableCell style={{ fontWeight: "bold" }}>
                    {activeTab === 0 ? "Data & Horário" : "Finalidade"}
                  </TableCell>
                  <TableCell style={{ fontWeight: "bold" }}>
                    {activeTab === 0 ? "Atendimento & Médico" : "Status Inicial"}
                  </TableCell>
                  {activeTab === 0 && (
                    <TableCell align="center" style={{ fontWeight: "bold" }}>
                      <Tooltip title="Clique para ativar/desativar o informativo para todas as pacientes da lista">
                        <span
                          onClick={handleToggleAllPregnant}
                          style={{
                            cursor: "pointer",
                            textDecoration: "underline",
                            textDecorationStyle: "dotted",
                            userSelect: "none"
                          }}
                        >
                          🤰 Gestante / USG?
                        </span>
                      </Tooltip>
                    </TableCell>
                  )}
                  <TableCell align="center" style={{ fontWeight: "bold" }}>Preview</TableCell>
                  <TableCell align="center" style={{ fontWeight: "bold" }}>Ação</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {appointments.map((item, idx) => (
                  <TableRow key={idx} hover style={{ backgroundColor: item.status === "sent" ? "#f0fdf4" : (item.isPregnant ? "#fdf4ff" : "inherit") }}>
                    <TableCell style={{ fontWeight: 600 }}>{item.name || "Não identificado"}</TableCell>
                    <TableCell style={{ fontFamily: "monospace" }}>{item.phone || "-"}</TableCell>
                    <TableCell>
                      {activeTab === 0 ? (
                        <>
                          <span style={{ fontWeight: 500 }}>{item.date || "Em breve"}</span>{" "}
                          {item.weekday && <span style={{ color: "#64748b", fontSize: "0.85rem" }}>({item.weekday})</span>}
                          <br />
                          <strong style={{ color: "#0284c7" }}>⏰ {item.time || "A confirmar"}</strong>
                        </>
                      ) : (
                        <Chip
                          size="small"
                          label="🎯 Resgate de Paciente"
                          style={{ backgroundColor: "#f3e8ff", color: "#7e22ce", fontWeight: "bold" }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {activeTab === 0 ? (
                        <>
                          <Chip
                            size="small"
                            label={item.serviceType || "Consulta"}
                            style={{ backgroundColor: "#e0f2fe", color: "#0369a1", fontWeight: "bold", marginRight: 6 }}
                          />
                          {item.notes && (
                            <Chip
                              size="small"
                              label={item.notes}
                              style={{ backgroundColor: "#fef3c7", color: "#92400e", fontWeight: "bold", marginRight: 6 }}
                            />
                          )}
                          <div style={{ fontSize: "0.82rem", color: "#475569", marginTop: 4 }}>
                            🩺 {item.professional || "Não especificado"}
                          </div>
                        </>
                      ) : (
                        <Chip
                          size="small"
                          label="🟡 Resgate"
                          style={{ backgroundColor: "#fef08a", color: "#854d0e", fontWeight: "bold" }}
                        />
                      )}
                    </TableCell>
                    {activeTab === 0 && (
                      <TableCell align="center">
                        <Tooltip title={item.isPregnant ? "Clique para remover informativo" : "Clique para incluir informativo (Pré-Natal / USG)"}>
                          <Chip
                            size="small"
                            clickable
                            onClick={() => handleTogglePregnant(idx)}
                            label={item.isPregnant ? "🤰 Sim (Ativo)" : "Não"}
                            style={{
                              cursor: "pointer",
                              fontWeight: "bold",
                              backgroundColor: item.isPregnant ? "#fae8ff" : "#f1f5f9",
                              color: item.isPregnant ? "#86198f" : "#64748b",
                              border: item.isPregnant ? "1px solid #d946ef" : "1px solid #cbd5e1"
                            }}
                          />
                        </Tooltip>
                      </TableCell>
                    )}
                    <TableCell align="center">
                      <Tooltip title="Ver mensagem completa">
                        <IconButton size="small" onClick={() => setPreviewMsg(item.formattedMessage)}>
                          <VisibilityIcon fontSize="small" style={{ color: "#64748b" }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      {item.status === "sent" ? (
                        <Chip
                          icon={<CheckCircleIcon style={{ color: "#16a34a" }} />}
                          label="Enviado"
                          style={{ backgroundColor: "#dcfce7", color: "#15803d", fontWeight: "bold" }}
                        />
                      ) : (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleSendSingle(idx)}
                          disabled={sendingIndex === idx || sendingBatch}
                          startIcon={sendingIndex === idx ? <CircularProgress size={16} color="inherit" /> : <SendIcon fontSize="small" />}
                          style={{
                            backgroundColor: activeTab === 0 ? "#7c3aed" : "#0284c7",
                            color: "#fff",
                            fontWeight: "bold"
                          }}
                        >
                          {sendingIndex === idx ? "Enviando..." : "Enviar"}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
          </>
        )}
      </Paper>

      {/* MODAL DE PREVIEW DA MENSAGEM */}
      <Dialog open={Boolean(previewMsg)} onClose={() => setPreviewMsg(null)} maxWidth="sm" fullWidth>
        <DialogTitle style={{ fontWeight: "bold" }}>📱 Pré-visualização da Mensagem</DialogTitle>
        <DialogContent>
          <div className={classes.previewBox}>
            {previewMsg}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewMsg(null)} color="primary" variant="contained">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default ConfirmarAgenda;
